import React from 'react';
import { Box, Typography } from '@mui/material';
import { dashboardTokens } from '../../../Dashboard/Components/dashboardCardStyles';
import { CampaignAggregate } from '../metaSelectors';

const BAR_FILL = '#a855f7';

interface TopCampaignsProps {
  campaigns: CampaignAggregate[];
  loading: boolean;
  limit?: number;
}

export const TopCampaigns: React.FC<TopCampaignsProps> = ({
  campaigns,
  loading,
  limit = 4,
}) => {
  const top = campaigns.slice(0, limit);
  // Bars are proportional to the leader, not to the total, so the strongest
  // campaign always fills the track and the rest read as a share of it.
  const max = top.length ? Math.max(...top.map((c) => c.leads)) : 0;

  return (
    <Box
      sx={{
        flex: '1 1 300px',
        minWidth: 280,
        bgcolor: dashboardTokens.cardBg,
        border: `1px solid ${dashboardTokens.border}`,
        borderRadius: dashboardTokens.cardRadius,
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>
          Top Campaigns
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: dashboardTokens.textSecondary, mt: 0.25 }}>
          By number of leads
        </Typography>
      </Box>

      {loading ? (
        <Typography sx={{ fontSize: 13, color: dashboardTokens.textMuted }}>Loading…</Typography>
      ) : top.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: dashboardTokens.textMuted }}>
          No campaign activity in this range yet.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
          {top.map((campaign) => (
            <Box key={campaign.campaignName} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 1.5,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: '#fff',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={campaign.campaignName}
                  >
                    {campaign.campaignName}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: dashboardTokens.textSecondary }}>
                    {campaign.qualified} qualified
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                  <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#fff', lineHeight: 1.1 }}>
                    {campaign.leads}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: dashboardTokens.textSecondary }}>
                    leads
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ height: 4, borderRadius: 2, bgcolor: '#2a2a2a', overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    borderRadius: 2,
                    bgcolor: BAR_FILL,
                    width: `${max ? Math.round((campaign.leads / max) * 100) : 0}%`,
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
